import { useRef, useState, type KeyboardEvent } from "react";
import { Button, Popover } from "@omnabase/ui";
import { SITE_FIELD_VALUE_MAX, type ReplyField } from "@omnabase/shared";

/** The site-fields round (2026-10-29; brief rule 4 "Writing", the red team's finding 11): the values a reply carries back to the ticket's site
 *  for its declared reply fields (kriesi.at's "Private data"), kept by the ticket page per ticket number. */
export type SiteFieldsChoice = { declared: ReplyField[]; values: Record<string, string>; onChange: (values: Record<string, string>) => void };

/** the part's words: one field its label, "<label> filled" once typed; several "Site fields", then "N field(s) filled" */
export function siteFieldsWords(declared: ReplyField[], values: Record<string, string>): string {
  const filled = declared.filter((f) => values[f.key]?.trim()).length;
  if (declared.length === 1) return filled ? `${declared[0]!.label} filled` : declared[0]!.label;
  return filled ? `${filled} ${filled === 1 ? "field" : "fields"} filled` : "Site fields";
}

/** The send line's part between the signature and "then …": a button that opens a small pop-up (360 px) with one plain textarea per
 *  declared field and "Done". Barebones as asked: no formatting, no files, no mentions. Esc closes the pop-up only, keeping the text (the
 *  key goes no further, so the composer and the ticket stay open). */
export function SiteFieldsPart({ siteFields: s }: { siteFields: SiteFieldsChoice }) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const words = siteFieldsWords(s.declared, s.values);
  const title = s.declared.length === 1 ? s.declared[0]!.label : "Site fields";
  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); setOpen(false); } };
  return (
    <>
      <button ref={button} type="button" className="ob-sendline__btn ob-sendline__fields" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{words}</button>
      {open && (
        <Popover label={title} anchor={button} onClose={() => setOpen(false)} width={360}>
          <div className="ob-sitefields" onKeyDown={onKey}>
            {s.declared.map((f, i) => (
              <label key={f.key} className="ob-sitefields__field">
                <span className="ob-sitefields__label">{f.label}</span>
                <textarea className="ob-sitefields__text" value={s.values[f.key] ?? ""} maxLength={SITE_FIELD_VALUE_MAX} autoFocus={i === 0} onChange={(e) => s.onChange({ ...s.values, [f.key]: e.target.value })} />
              </label>
            ))}
            <div className="ob-popover__actions"><Button size="sm" variant="primary" onClick={() => setOpen(false)}>Done</Button></div>
          </div>
        </Popover>
      )}
    </>
  );
}

/** the reply's `fields`: the declared keys with something typed, in the declaration's order */
export const siteFieldValues = (declared: ReplyField[], values: Record<string, string>): { key: string; value: string }[] =>
  declared.filter((f) => values[f.key]?.trim()).map((f) => ({ key: f.key, value: values[f.key]! }));
